<template>
  <div
    class="bg-static-default-low border-static-default-low rounded-lg relative border p-24"
    @dragenter.prevent
    @dragleave.prevent
    @dragover.prevent
    @drop.prevent
  >
    <ErrorInfoBlock v-if="isError" @try-again="send('TRY_AGAIN')" />
    <Loading v-if="isLoading" />

    <ActionsList
      v-if="state.matches('actionsList')"
      :actions="state.context.actions"
      :pendingProblems="state.context.pendingProblems"
      :requests
      :send
      :state
    />

    <ShipBackFlow
      v-if="state.matches('shipBackFlow')"
      :mostUrgentTask
      @back="send('BACK')"
      @done="handleItemShippedBack"
      @exit="send('EXIT')"
    />

    <RefundFlow
      v-if="state.matches('refundFlow') && state.context.refundInfo"
      :mostUrgentTask
      :refundInfo="state.context.refundInfo"
      :send
      :state
      @done="handleRefundCreated"
    />

    <SendMessageFlow
      v-if="state.matches('sendMessageFlow')"
      :mostUrgentTask
      @back="send('BACK')"
      @done="handleMessageSent"
      @exit="send('EXIT')"
    />

    <ReportProblemFlow
      v-if="state.matches('reportProblemFlow')"
      :mostUrgentTask
      :problemTypes="state.context.reportProblemInfo.problemTypes"
      :send
      :shouldShowSendMessage="
        state.context.reportProblemInfo.hasSelectedSendMessage
      "
      :state
      @done="handleProblemReported"
      @exit="send('EXIT')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, shallowRef } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { interpret } from 'xstate'

import { getMostUrgentTask } from '~/scopes/customer-care/components/request.helpers'

import ErrorInfoBlock from '../../../../../../components/ErrorInfoBlock/ErrorInfoBlock.vue'
import { useOrderlinePublicIdFromRoute } from '../../../../composables/useOrderlinePublicIdFromRoute'

import { actionBarMachine } from './ActionBar.machine'
import ActionsList from './components/ActionsList/ActionsList.vue'
import Loading from './components/Loading/Loading.vue'
import RefundFlow from './components/RefundFlow/RefundFlow.vue'
import ReportProblemFlow from './components/ReportProblemFlow/ReportProblemFlow.vue'
import SendMessageFlow from './components/SendMessageFlow/SendMessageFlow.vue'
import ShipBackFlow from './components/ShipBackFlow/ShipBackFlow.vue'

const orderlinePublicId = useOrderlinePublicIdFromRoute()

const emit = defineEmits([
  'refund-created',
  'item-shipped-back',
  'message-sent',
  'problem-reported',
])

const state = shallowRef(actionBarMachine.initialState)

const $httpFetch = useHttpFetch()

const service = interpret(
  actionBarMachine.withConfig({
    services: {
      fetchActionsAndRequests: async () => {
        return await Promise.all([
          $httpFetch(
            '/seller-after-sales/api/v2/orderlines/:orderlinePublicId/actions',
            {
              pathParams: {
                orderlinePublicId: orderlinePublicId.value,
              },
            },
          ),
          $httpFetch(
            '/seller-after-sales/api/v1/orderlines/:orderlinePublicId/requests',
            {
              pathParams: {
                orderlinePublicId: orderlinePublicId.value,
              },
            },
          ),
        ])
      },
    },
  }),
  {
    devTools: false,
  },
).onTransition((newState) => {
  state.value = newState
})

const { send } = service

onMounted(() => {
  service.start()
})

onUnmounted(() => {
  service.stop()
})

const isLoading = computed(() => state.value.matches('initialFetch.loading'))
const isError = computed(() => state.value.matches('initialFetch.error'))

const requests = computed(() => state.value.context.requests)

const mostUrgentTask = computed(() => getMostUrgentTask(requests.value, null))

function handleMessageSent() {
  send('DONE')
  emit('message-sent')
}

function handleProblemReported() {
  send('DONE')
  emit('problem-reported')
}

function handleItemShippedBack() {
  send('DONE')
  emit('item-shipped-back')
}

function handleRefundCreated() {
  send('DONE')
  emit('refund-created')
}
</script>
